import { diagnostics } from '@/utils/diagnostics';

/**
 * Finds the nearest scrollable parent element of a given DOM element.
 * A scrollable element is one that has overflow or overflowY set to 'auto' or 'scroll'.
 *
 * The function traverses up the DOM tree and checks each parent's computed style.
 * It handles special cases like absolute positioning and falls back to document.documentElement
 * if no scrollable parent is found.
 *
 * @param element - The DOM element to find the scrollable parent for
 * @returns The nearest scrollable parent element, or document.documentElement if none found
 *
 * @example
 * const element = document.querySelector('.my-element');
 * const scrollParent = getScrollParent(element);
 * scrollParent.scrollTo({ top: 100, behavior: 'smooth' });
 */
export function getScrollParent(element: HTMLElement): HTMLElement {
  const style = window.getComputedStyle(element);
  const excludeStaticParent = style.position === 'absolute';
  const overflowRegex = /(auto|scroll)/;

  if (excludeStaticParent && style.position === 'static') {
    return getScrollParent(element.parentElement!);
  }

  let parent = element.parentElement;

  while (parent) {
    const style = window.getComputedStyle(parent);
    if (overflowRegex.test(style.overflow + style.overflowY)) {
      diagnostics.debug('[Annotation] Found scroll parent', {
        parent,
        overflow: style.overflow,
        overflowY: style.overflowY,
      });
      return parent;
    }
    parent = parent.parentElement;
  }

  diagnostics.debug(
    '[Annotation] No scroll parent found, using document.documentElement'
  );
  return document.documentElement;
}
